import * as THREE from "three";
import { CHUNK_SIZE, DAY_LENGTH, MAX_HEALTH } from "./config";
import { Block, blockDef } from "./engine/blocks";
import { Input } from "./engine/input";
import { createAtlas } from "./engine/textures";
import { World, worldToChunk, worldToLocal } from "./engine/world";
import { Sfx } from "./audio/sfx";
import { Containers } from "./game/containers";
import { ItemDrops } from "./game/drops";
import { MobWorld } from "./game/mobs";
import { Player } from "./game/player";
import { applySave, clearSave, collectSave, loadSave, persist } from "./game/save";
import { findSpawn } from "./gen/terrain";
import { Item, canHarvest, itemDef } from "./game/items";
import { BlockHighlight, BreakCrack } from "./render/highlight";
import { Particles } from "./render/particles";
import { createSkyDome } from "./render/shaders";
import { WorldView } from "./render/worldView";
import { Hud, blockColor } from "./ui/hud";
import type { RayHit } from "./engine/raycast";

type Mode = "title" | "loading" | "playing" | "paused";

function parseSeed(raw: string): number {
  const n = Number(raw);
  if (Number.isFinite(n) && raw.trim() !== "") return n >>> 0;
  let h = 2166136261;
  for (let i = 0; i < raw.length; i++) {
    h ^= raw.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function randomSeed(): string {
  return String(Math.floor(Math.random() * 1_000_000_000));
}

class Game {
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private input: Input;
  private atlas = createAtlas();
  private world: World;
  private view: WorldView;
  private player = new Player();
  private hud: Hud;
  private containers = new Containers();
  private mobs = new MobWorld();
  private drops = new ItemDrops();
  private particles = new Particles();
  private highlight = new BlockHighlight();
  private crack = new BreakCrack();
  private sfx = new Sfx();
  private sky = createSkyDome();
  private sun = new THREE.DirectionalLight(0xfff1d0, 1.15);
  private hemi = new THREE.HemisphereLight(0xcfe8ff, 0x6a5a3a, 0.72);
  private clock = new THREE.Clock();
  private mode: Mode = "title";
  private worldTime = 80;
  private fps = 60;
  private fpsAcc = 0;
  private frames = 0;
  private wasInWater = false;
  private stepAcc = 0;
  private saveTimer = 0;
  private justPressed = new Set<string>();
  private skyMat: THREE.ShaderMaterial;

  constructor() {
    const canvas = document.getElementById("view") as HTMLCanvasElement;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.setSize(innerWidth, innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.camera = new THREE.PerspectiveCamera(68, innerWidth / innerHeight, 0.08, 500);
    this.input = new Input(canvas);
    this.scene.fog = new THREE.FogExp2(0xb8d4e0, 0.0075);
    this.scene.add(this.sky, this.sun, this.hemi);
    this.sun.position.set(60, 90, 40);
    this.skyMat = this.sky.material as THREE.ShaderMaterial;

    const seed = Number(new URLSearchParams(location.search).get("seed")) || parseSeed(randomSeed());
    this.world = new World(seed);
    const worker = new Worker(new URL("./workers/terrain.worker.ts", import.meta.url), { type: "module" });
    this.view = new WorldView(this.world, this.atlas, worker);
    this.scene.add(this.view.group, this.player.character.group, this.mobs.group, this.drops.group);
    this.scene.add(this.particles.group, this.highlight.mesh, this.crack.mesh);

    this.hud = new Hud(this.atlas, this.player.inventory, () => this.closeUi());
    this.hud.showTitle(!!loadSave(), String(seed));
    this.hud.playingHud(false);
    this.hud.onEnter(() => this.startNew());
    this.hud.onContinue(() => this.continueSave());
    this.hud.onResume(() => this.resume());
    this.hud.onSave(() => this.save());
    this.hud.onLeave(() => this.leave());

    window.addEventListener("resize", this.onResize);
    window.addEventListener("keydown", this.onKey);
    canvas.addEventListener("click", () => {
      if (this.mode === "playing" && this.player.open === "none") this.input.lock();
    });

    this.giveStarter();
    this.view.requestAround(0, 0);
    this.view.pump(3);
    this.loop();
  }

  private giveStarter(): void {
    this.player.inventory.add(Block.OAK_PLANKS, 16);
    this.player.inventory.add(Block.CRAFTING_TABLE, 1);
    this.player.inventory.add(Block.OAK_LOG, 8);
    this.player.inventory.add(Item.STICK, 8);
  }

  private startNew(): void {
    const seed = parseSeed(this.hud.seedValue() || randomSeed());
    clearSave();
    this.bootWorld(seed, null);
  }

  private continueSave(): void {
    const data = loadSave();
    if (!data) {
      this.startNew();
      return;
    }
    this.bootWorld(data.seed, data);
  }

  private bootWorld(seed: number, save: ReturnType<typeof loadSave>): void {
    this.scene.remove(this.view.group);
    this.scene.remove(this.player.character.group);
    this.view.dispose();
    this.world = new World(seed);
    const worker = new Worker(new URL("./workers/terrain.worker.ts", import.meta.url), { type: "module" });
    this.view = new WorldView(this.world, this.atlas, worker);
    this.scene.add(this.view.group);
    this.player = new Player();
    this.containers = new Containers();
    this.giveStarter();
    if (save) applySave(save, this.world, this.player, this.containers);
    else {
      const spawn = findSpawn(seed);
      this.player.spawn(spawn.x, spawn.y, spawn.z);
    }
    this.scene.add(this.player.character.group);
    this.hud = new Hud(this.atlas, this.player.inventory, () => this.closeUi());
    this.wireHud();
    this.mode = "loading";
    this.hud.hideTitle();
    this.hud.setLoading("Growing continents...");
    this.hud.playingHud(false);
    this.view.requestAround(this.player.x, this.player.z);
    this.view.pump(4);
  }

  private wireHud(): void {
    this.hud.onEnter(() => this.startNew());
    this.hud.onContinue(() => this.continueSave());
    this.hud.onResume(() => this.resume());
    this.hud.onSave(() => this.save());
    this.hud.onLeave(() => this.leave());
  }

  private enterPlay(): void {
    this.mode = "playing";
    this.hud.setLoading(null);
    this.hud.playingHud(true);
    this.input.lock();
    this.sfx.ensure();
  }

  private pause(): void {
    if (this.mode !== "playing") return;
    this.closeUi();
    this.mode = "paused";
    this.input.unlock();
    this.hud.setPaused(true);
  }

  private resume(): void {
    this.hud.setPaused(false);
    this.mode = "playing";
    this.input.lock();
  }

  private leave(): void {
    this.save();
    this.mode = "title";
    this.hud.setPaused(false);
    this.hud.playingHud(false);
    this.hud.showTitle(true, String(this.world.seed));
    this.input.unlock();
  }

  private save(): void {
    persist(collectSave(this.world, this.player, this.containers));
    this.sfx.ui();
  }

  private closeUi(): void {
    if (this.player.open === "none") return;
    this.player.open = "none";
    this.player.openPos = null;
    this.hud.closePanel();
    this.input.lock();
  }

  private toggleInventory(): void {
    if (this.player.open !== "none") {
      this.closeUi();
      return;
    }
    this.player.open = "inventory";
    this.input.unlock();
    this.hud.openInventory(this.player);
    this.sfx.ui();
  }

  private onKey = (e: KeyboardEvent): void => {
    if (e.repeat) return;
    this.justPressed.add(e.code);
    if (e.code === "Escape") {
      if (this.player.open !== "none") this.closeUi();
      else if (this.mode === "playing") this.pause();
      else if (this.mode === "paused") this.resume();
    }
    if (this.mode !== "playing") return;
    if (e.code === "KeyE") this.toggleInventory();
    if (e.code === "KeyF") {
      this.player.flying = !this.player.flying;
      this.player.vy = 0;
      this.sfx.ui();
    }
    if (e.code === "KeyV") this.player.thirdPerson = !this.player.thirdPerson;
    if (e.code === "KeyQ") this.dropSelected();
  };

  private dropSelected(): void {
    const s = this.player.inventory.selectedSlot();
    if (!s) return;
    this.player.inventory.consumeSelected(1);
    const dir = this.player.lookDir();
    this.drops.spawn(this.player.x + dir.x, this.player.y + 1.2, this.player.z + dir.z, s.id, 1);
  }

  private tryHitMob = (): boolean => {
    const eye = this.player.eye();
    const dir = this.player.lookDir();
    const mob = this.mobs.pick(eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, 4.2);
    if (!mob) return false;
    const held = this.player.inventory.selectedSlot();
    const dmg = held && itemDef(held.id).tool === "sword" ? (itemDef(held.id).power ?? 4) : 2;
    this.mobs.strike(mob, dmg, this.player.x, this.player.z);
    if (held && itemDef(held.id).durability) this.player.inventory.damageSelected(1);
    this.sfx.hurt();
    return true;
  }

  private onBreak = (hit: RayHit): void => {
    const id = this.world.getBlock(hit.x, hit.y, hit.z);
    if (blockDef(id).hardness < 0) return;
    const tool = this.player.inventory.selectedSlot();
    const harvest = canHarvest(id, tool ? tool.id : null);
    this.world.setBlock(hit.x, hit.y, hit.z, Block.AIR);
    this.remeshAround(hit.x, hit.z);
    this.particles.burst(hit.x, hit.y, hit.z, blockColor(id));
    this.sfx.break();
    if (harvest) {
      for (const drop of blockDef(id).drops) {
        if (Math.random() <= drop.chance) this.drops.spawn(hit.x + 0.5, hit.y + 0.4, hit.z + 0.5, drop.item, drop.count);
      }
    }
    for (const extra of this.containers.remove(hit.x, hit.y, hit.z)) {
      if (extra) this.drops.spawn(hit.x + 0.5, hit.y + 0.6, hit.z + 0.5, extra.id, extra.count);
    }
  };

  private onPlace = (x: number, y: number, z: number, id: number): void => {
    this.world.setBlock(x, y, z, id);
    this.remeshAround(x, z);
    this.sfx.place();
  };

  private onInteract = (hit: RayHit): void => {
    const id = this.world.getBlock(hit.x, hit.y, hit.z);
    this.player.openPos = { x: hit.x, y: hit.y, z: hit.z };
    this.input.unlock();
    if (id === Block.CRAFTING_TABLE) {
      this.player.open = "crafting";
      this.hud.openTable(this.player);
    } else if (id === Block.CHEST) {
      this.player.open = "chest";
      this.hud.openChest(this.player, this.containers.chest(hit.x, hit.y, hit.z));
    } else if (id === Block.FURNACE) {
      this.player.open = "furnace";
      this.hud.openFurnace(this.player, this.containers.furnace(hit.x, hit.y, hit.z));
    }
    this.sfx.ui();
  };

  private dayColors(): void {
    const t = (this.worldTime / DAY_LENGTH) % 1;
    const ang = t * Math.PI * 2;
    const sunH = Math.sin(ang);
    this.sun.position.set(Math.cos(ang) * 120, Math.max(8, sunH * 110), 40);
    const day = sunH > 0.05;
    const dusk = sunH > -0.15 && sunH < 0.25;
    const top = day ? 0x6eb6e0 : 0x0e1a2e;
    const mid = day ? 0xc8e4f2 : 0x1c2c48;
    const bot = dusk ? 0xe0a070 : day ? 0xe8d8b8 : 0x141820;
    this.skyMat.uniforms.top!.value.setHex(top);
    this.skyMat.uniforms.mid!.value.setHex(mid);
    this.skyMat.uniforms.bot!.value.setHex(bot);
    const fog = dusk ? 0xd8a878 : day ? 0xb8d4e0 : 0x101824;
    (this.scene.fog as THREE.FogExp2).color.setHex(fog);
    this.sun.intensity = day ? 1.15 : 0.12;
    this.hemi.intensity = day ? 0.72 : 0.22;
    const cam = this.camera.position;
    if (this.world.isWater(Math.floor(cam.x), Math.floor(cam.y), Math.floor(cam.z))) {
      (this.scene.fog as THREE.FogExp2).color.setHex(0x1a6e88);
      (this.scene.fog as THREE.FogExp2).density = 0.045;
    } else {
      (this.scene.fog as THREE.FogExp2).density = 0.0075;
    }
  }

  private loop = (): void => {
    requestAnimationFrame(this.loop);
    const dt = Math.min(0.05, this.clock.getDelta());
    this.frames++;
    this.fpsAcc += dt;
    if (this.fpsAcc >= 0.4) {
      this.fps = Math.round(this.frames / this.fpsAcc);
      this.frames = 0;
      this.fpsAcc = 0;
    }

    if (this.mode === "title") {
      this.worldTime += dt;
      this.view.requestAround(0, 0);
      this.view.pump(2);
      this.view.remeshDirty(4);
      this.view.tick(this.clock.elapsedTime);
      this.dayColors();
      this.camera.position.set(Math.sin(this.worldTime * 0.08) * 28, 78, Math.cos(this.worldTime * 0.08) * 28);
      this.camera.lookAt(0, 54, 0);
      this.renderer.render(this.scene, this.camera);
      return;
    }

    this.view.requestAround(this.player.x, this.player.z);
    this.view.pump(this.mode === "loading" ? 4 : 2);
    this.view.remeshDirty(this.mode === "loading" ? 6 : 3);
    this.view.tick(this.clock.elapsedTime);

    if (this.mode === "loading") {
      this.hud.setLoading(`Sculpting chunks ${this.view.loaded}/${this.view.wanted}`);
      if (this.view.readyNear(this.player.x, this.player.z, 2)) this.enterPlay();
      this.player.applyCamera(this.camera, this.world);
      this.renderer.render(this.scene, this.camera);
      return;
    }

    const playing = this.mode === "playing" && this.player.open === "none";
    this.player.update(
      dt,
      this.input,
      this.world,
      this.containers,
      playing,
      this.onBreak,
      this.onPlace,
      this.onInteract,
      this.tryHitMob,
    );
    if (this.mode === "playing") {
      this.worldTime += dt;
      this.containers.tick(dt);
      this.mobs.update(dt, this.world, this.player, (x, y, z, id) => this.drops.spawn(x, y, z, id));
      this.drops.update(dt, this.player.x, this.player.y, this.player.z, this.player.inventory);
      this.particles.update(dt);
      this.highlight.set(this.player.open === "none" ? this.player.target : null);
      this.crack.set(this.player.breakTarget, this.player.breakTime);

      const inWater = this.player.swimming;
      if (inWater && !this.wasInWater) this.sfx.splash();
      this.wasInWater = inWater;
      if (this.player.grounded && (Math.abs(this.player.vx) > 0.4 || Math.abs(this.player.vz) > 0.4)) {
        this.stepAcc += dt;
        if (this.stepAcc > (this.player.sprinting ? 0.28 : 0.4)) {
          this.stepAcc = 0;
          this.sfx.step();
        }
      }

      if (this.player.open === "furnace" && this.player.openPos) {
        const f = this.containers.furnace(this.player.openPos.x, this.player.openPos.y, this.player.openPos.z);
        this.hud.tickFurnace(f);
      }

      this.saveTimer += dt;
      if (this.saveTimer > 20) {
        this.saveTimer = 0;
        this.save();
      }

      if (this.player.health <= 0) {
        this.player.health = MAX_HEALTH;
        this.player.hunger = 14;
        const spawn = findSpawn(this.world.seed);
        this.player.spawn(spawn.x, spawn.y, spawn.z);
        this.sfx.hurt();
      }
    }

    this.player.applyCamera(this.camera, this.world);
    this.sky.position.copy(this.camera.position);
    this.dayColors();

    if (this.mode === "playing") {
      this.hud.updatePlay(
        this.player,
        this.world.biomeAt(Math.floor(this.player.x), Math.floor(this.player.z)),
        this.fps,
        `${this.view.loaded} chunks`,
      );
    }

    this.renderer.render(this.scene, this.camera);
    this.justPressed.clear();
  };

  private remeshAround(x: number, z: number): void {
    const cx = worldToChunk(x);
    const cz = worldToChunk(z);
    const lx = worldToLocal(x);
    const lz = worldToLocal(z);
    this.view.remesh(cx, cz);
    if (lx === 0) this.view.remesh(cx - 1, cz);
    if (lx === CHUNK_SIZE - 1) this.view.remesh(cx + 1, cz);
    if (lz === 0) this.view.remesh(cx, cz - 1);
    if (lz === CHUNK_SIZE - 1) this.view.remesh(cx, cz + 1);
  }

  private onResize = (): void => {
    this.camera.aspect = innerWidth / innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(innerWidth, innerHeight);
  };
}

new Game();
